import React, { ReactNode, useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { useSessionEnforcement } from '../../hooks/useSessionEnforcement';
import { useAuth } from '../../context/AuthContext';
import { startIdleTimer, stopIdleTimer } from '../../lib/idleTimeout';
import { setDeviceToken, getDeviceName } from '../../lib/deviceTrust';
import { supabase } from '../../supabaseClient';
import IdleLockScreen from '../auth/IdleLockScreen';
import SMSVerification from '../auth/SMSVerification';

interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useSessionEnforcement();
  const { profile } = useAuth();
  const [showLockScreen, setShowLockScreen] = useState(false);
  const [mfaPending, setMfaPending] = useState<{ phone: string; userId: string; orgId: string } | null>(null);

  // No-phone banner logic
  const DISMISS_KEY = 'phone_banner_dismissed_until';
  const [phoneBannerDismissed, setPhoneBannerDismissed] = useState(
    () => Date.now() < Number(localStorage.getItem(DISMISS_KEY) || '0')
  );
  const showPhoneBanner = false; // TEMPORARY: MFA on hold - hide phone banner until MFA re-enabled
  // Original: const showPhoneBanner = !!profile
  // && profile.role !== 'SUPER_ADMIN'
  // && !profile.phone
  // && !mfaPending
  // && !phoneBannerDismissed;

  // Idle timeout
  useEffect(() => {
    startIdleTimer(1800000, () => setShowLockScreen(true));
    return () => stopIdleTimer();
  }, []);

  // MFA fallback — pick up pending verification if LoginForm redirected before SMS completed
  useEffect(() => {
    const raw = sessionStorage.getItem('mfa_pending');
    if (raw) {
      try {
        setMfaPending(JSON.parse(raw));
      } catch {
        sessionStorage.removeItem('mfa_pending');
      }
    }
  }, []);

  const handleMFAVerified = async (deviceToken: string) => {
    if (mfaPending) {
      try {
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

        await supabase.from('trusted_devices').insert({
          user_id: mfaPending.userId,
          device_token: deviceToken,
          device_name: getDeviceName(),
          organization_id: mfaPending.orgId,
          expires_at: thirtyDaysFromNow.toISOString(),
        });

        setDeviceToken(deviceToken);
      } catch (err) {
        console.error('Failed to save trusted device:', err);
        setDeviceToken(deviceToken);
      }
    }
    sessionStorage.removeItem('mfa_pending');
    setMfaPending(null);
  };

  const handleMFACancel = async () => {
    sessionStorage.removeItem('mfa_pending');
    setMfaPending(null);
    await supabase.auth.signOut();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="flex min-h-screen bg-surface-bg relative">
      {showPhoneBanner && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-status-warning-light border-b border-status-warning/30 px-4 py-3">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <p className="text-sm text-text-primary">
              Add a phone number in{' '}
              <a href="/settings" className="font-semibold text-accent-blue hover:underline">Settings</a>
              {' '}to enable two-factor authentication.
            </p>
            <button
              type="button"
              onClick={() => {
                localStorage.setItem(DISMISS_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
                setPhoneBannerDismissed(true);
              }}
              className="ml-4 text-text-muted hover:text-text-primary transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Sidebar - Pass state and toggle function */}
      <Sidebar isMobileMenuOpen={isMobileMenuOpen} toggleMobileMenu={toggleMobileMenu} />

      {/* Main content area - Adjust margin for responsiveness */}
      <div className="flex-1 flex flex-col overflow-hidden md:ml-16 xl:ml-64">
        {/* Header - Pass toggle function */}
        <Header toggleMobileMenu={toggleMobileMenu} />

        {/* Page Content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-surface-bg p-4 sm:p-6">
          {children}
        </main>
      </div>

      {showLockScreen && <IdleLockScreen onUnlock={() => setShowLockScreen(false)} />}

      {mfaPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0D1827]">
          <div className="w-full max-w-sm mx-4 bg-white rounded-2xl shadow-xl p-8">
            <SMSVerification
              phone={mfaPending.phone}
              onVerified={handleMFAVerified}
              onCancel={handleMFACancel}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MainLayout;

export {};
